/**
 * AdminDevicesSection - IoT device token management
 * @idempotent @atomic
 */
import { memo, useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { labelClasses, getInputClasses, baseInputClasses } from '../../styles/inputStyles'
import { toast } from '../../services/toastService'

const logger = createLogger('AdminDevicesSection')

const AdminDevicesSection = memo(function AdminDevicesSection() {
  const { t } = useTranslation()
  const { loading, execute } = useAsyncOperation()

  const [devices, setDevices] = useState([])
  const [newToken, setNewToken] = useState(null)
  const [form, setForm] = useState({ entityId: '', name: '' })
  const [errors, setErrors] = useState({})

  // Entity search state
  const [entityQuery, setEntityQuery] = useState('')
  const [entityResults, setEntityResults] = useState([])
  const [selectedEntityName, setSelectedEntityName] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef(null)
  const searchRef = useRef(null)

  useEffect(() => {
    loadDevices()
  }, [])

  const loadDevices = useCallback(async () => {
    await execute(() => adminAPI.listDevices(), {
      onSuccess: (res) => setDevices(res.data?.devices || []),
      onError: (e) => logger.error('Failed to load devices', e),
      silent: true,
    })
  }, [execute])

  const searchEntities = useCallback((query) => {
    clearTimeout(debounceRef.current)
    if (!query.trim()) {
      setEntityResults([])
      setShowSuggestions(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      await execute(() => adminAPI.getAllEntities({ search: query, limit: 10 }), {
        onSuccess: (res) => {
          setEntityResults(res.data?.data || [])
          setShowSuggestions(true)
        },
        onError: (e) => logger.error('Entity search failed', e),
        silent: true,
      })
    }, 300)
  }, [execute])

  const handleEntityQueryChange = useCallback((e) => {
    const q = e.target.value
    setEntityQuery(q)
    setSelectedEntityName('')
    setForm(prev => ({ ...prev, entityId: '' }))
    searchEntities(q)
  }, [searchEntities])

  const handleSelectEntity = useCallback((entity) => {
    setForm(prev => ({ ...prev, entityId: entity.id }))
    setSelectedEntityName(entity.name)
    setEntityQuery(entity.name)
    setEntityResults([])
    setShowSuggestions(false)
  }, [])

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleCreate = useCallback(async (e) => {
    e.preventDefault()
    const errs = {}
    if (!form.entityId) errs.entityId = t('admin.devices.errorEntityRequired', 'Entity is required')
    if (!form.name.trim()) errs.name = t('admin.devices.errorNameRequired', 'Name is required')
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})

    await execute(() => adminAPI.createDevice(form.entityId, form.name.trim()), {
      onSuccess: (res) => {
        setNewToken(res.data?.token || res.data?.data?.token)
        setForm({ entityId: '', name: '' })
        setEntityQuery('')
        setSelectedEntityName('')
        loadDevices()
        toast.success(t('admin.devices.created', 'Device token created'))
      },
      onError: (e) => toast.error(e.message || 'Failed to create device'),
    })
  }, [execute, form, t, loadDevices])

  const handleRevoke = useCallback(async (id) => {
    if (!window.confirm(t('admin.devices.revokeConfirm', 'Revoke this device token?'))) return
    await execute(() => adminAPI.revokeDevice(id), {
      onSuccess: () => {
        loadDevices()
        toast.success(t('admin.devices.revoked', 'Device revoked'))
      },
      onError: (e) => toast.error(e.message || 'Failed to revoke device'),
    })
  }, [execute, t, loadDevices])

  const handleRename = useCallback(async (id, name) => {
    if (!name.trim()) return
    await execute(() => adminAPI.renameDevice(id, name.trim()), {
      onSuccess: () => {
        loadDevices()
        toast.success('Device renamed')
      },
      onError: (e) => toast.error(e.message || 'Failed to rename device'),
    })
  }, [execute, loadDevices])

  const handleReissue = useCallback(async (id) => {
    if (!window.confirm('Generate a new token? The current token will stop working immediately.')) return
    await execute(() => adminAPI.reissueToken(id), {
      onSuccess: (res) => {
        setNewToken(res.data?.token)
        toast.success('New token generated — copy it now')
      },
      onError: (e) => toast.error(e.message || 'Failed to reissue token'),
    })
  }, [execute])

  const handleCopyId = useCallback((id) => {
    navigator.clipboard.writeText(id).then(() => toast.success('Device ID copied'))
  }, [])

  const handleCopyToken = useCallback(() => {
    if (newToken) {
      navigator.clipboard.writeText(newToken).then(() => toast.success('Token copied!'))
    }
  }, [newToken])

  return (
    <div className="space-y-8">
      {/* Create form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.devices.create', 'Create Device Token')}</h2>
        <form onSubmit={handleCreate} className="space-y-4 max-w-md">
          <div ref={searchRef} className="relative">
            <label className={labelClasses}>{t('admin.devices.entity', 'Entity')}</label>
            <input
              type="text"
              placeholder={t('admin.devices.searchEntity', 'Search entity...')}
              className={getInputClasses(!!errors.entityId)}
              value={entityQuery}
              onChange={handleEntityQueryChange}
              onFocus={() => entityResults.length > 0 && setShowSuggestions(true)}
              autoComplete="off"
            />
            {showSuggestions && entityResults.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {entityResults.map(en => (
                  <li
                    key={en.id}
                    className="px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 cursor-pointer"
                    onMouseDown={() => handleSelectEntity(en)}
                  >
                    {en.name}
                  </li>
                ))}
              </ul>
            )}
            {form.entityId && (
              <p className="mt-1 text-xs text-green-600">✓ {selectedEntityName}</p>
            )}
            {errors.entityId && <p className="text-red-500 text-xs mt-1">{errors.entityId}</p>}
          </div>
          <div>
            <label className={labelClasses}>{t('admin.devices.name', 'Device name')}</label>
            <input
              type="text"
              placeholder={t('admin.devices.namePlaceholder', "e.g. 'Taco Truck Lamp'")}
              className={getInputClasses(!!errors.name)}
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              maxLength={100}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          <button
            type="submit"
            disabled={loading}
            onClick={handleCreate}
            onTouchEnd={handleCreate}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? t('app.loading', 'Loading...') : t('admin.devices.create', 'Create Device Token')}
          </button>
        </form>

        {/* One-time token display */}
        {newToken && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md max-w-md">
            <p className="text-sm font-medium text-yellow-800 mb-2">
              {t('admin.devices.tokenOnce', 'Copy this token now — it will not be shown again')}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-yellow-300 rounded px-2 py-1 break-all">{newToken}</code>
              <button
                onClick={handleCopyToken}
                onTouchEnd={handleCopyToken}
                className="px-3 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600"
              >
                {t('admin.devices.tokenCopy', 'Copy')}
              </button>
            </div>
            <button
              onClick={() => setNewToken(null)}
              onTouchEnd={() => setNewToken(null)}
              className="mt-2 text-xs text-yellow-700 underline"
            >
              {t('admin.devices.dismiss', 'Dismiss')}
            </button>
          </div>
        )}
      </div>

      {/* Device list */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.devices.title', 'IoT Devices')}</h2>
          <p className="text-sm text-gray-500">{t('admin.devices.subtitle', 'Manage ESP-32 device tokens for entity lamp control')}</p>
        </div>
        {devices.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500 text-sm">
            {t('admin.devices.noDevices', 'No devices configured yet')}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {devices.map(device => (
              <DeviceRow
                key={device.id}
                device={device}
                onRename={handleRename}
                onReissue={handleReissue}
                onRevoke={handleRevoke}
                onCopyId={handleCopyId}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
})

/**
 * DeviceRow - single device with inline rename + action menu
 * @idempotent @atomic
 */
const DeviceRow = memo(function DeviceRow({ device, onRename, onReissue, onRevoke, onCopyId, t }) {
  const [editing, setEditing] = useState(false)
  const [nameValue, setNameValue] = useState(device.name)

  const commitRename = useCallback(() => {
    setEditing(false)
    if (nameValue.trim() && nameValue.trim() !== device.name) {
      onRename(device.id, nameValue.trim())
    } else {
      setNameValue(device.name)
    }
  }, [nameValue, device.id, device.name, onRename])

  return (
    <div className="px-6 py-4">
      <div className="flex items-start justify-between gap-4">
        {/* Name + entity */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              autoFocus
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setNameValue(device.name); setEditing(false) } }}
              className="text-sm font-medium border-b border-primary outline-none bg-transparent w-full max-w-xs"
              maxLength={100}
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              onTouchEnd={() => setEditing(true)}
              className="text-sm font-medium text-gray-900 hover:text-primary text-left group"
              title="Click to rename"
            >
              {device.name}
              <span className="ml-1 text-gray-300 group-hover:text-primary text-xs">✎</span>
            </button>
          )}
          <p className="text-xs text-gray-500 mt-0.5">{device.entityName}</p>
          <p className="text-xs text-gray-400 mt-0.5 font-mono truncate max-w-xs" title={device.id}>{device.id}</p>
        </div>

        {/* Last seen */}
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">
            {device.lastSeen
              ? new Date(device.lastSeen).toLocaleString()
              : t('admin.devices.never', 'Never')}
          </p>
          <p className="text-xs text-gray-400">{t('admin.devices.lastSeen', 'Last seen')}</p>
        </div>
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={() => onCopyId(device.id)}
          onTouchEnd={() => onCopyId(device.id)}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Copy ID
        </button>
        <span className="text-gray-200">|</span>
        <button
          onClick={() => onReissue(device.id)}
          onTouchEnd={() => onReissue(device.id)}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Re-issue token
        </button>
        <span className="text-gray-200">|</span>
        <button
          onClick={() => onRevoke(device.id)}
          onTouchEnd={() => onRevoke(device.id)}
          className="text-xs text-red-600 hover:text-red-800 underline"
        >
          {t('admin.devices.revoke', 'Revoke')}
        </button>
      </div>
    </div>
  )
})

export default AdminDevicesSection
